import type { ItEmailPublic } from '@accounts/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { STR } from '../../../../i18n';
import {
  ApiError,
  createItEmail,
  markItEmailSent,
  takeBackItEmail,
  updateItEmailText,
} from '../../../../lib/api';
import { queryKeys } from '../../../../lib/queryKeys';
import { showToast } from '../../../../lib/toast';

import { checklistSummary, recordIdsOf } from './checklist';
import { ItEmailDialog } from './ItEmailDialog';
import type { ChecklistItEmailDraft } from './useChecklistItEmail';

/**
 * A CHECKLIST'S EMAIL TO IT (spec row 7; FINAL demo: "the existing Draft IT email now uses
 * the same email, with Mark as sent"). The same dialog as New email to IT: the text in
 * colour, Edit text / Done editing, Copy text. Save draft and Mark as sent keep one IT email
 * over exactly the records the text names (`draft.items`, fixed when it was drafted), so
 * the items move to Waiting on IT exactly as from the Queue. Accounts sends nothing itself.
 */
export function ChecklistItEmailDialog({
  draft,
  onClose,
}: {
  draft: ChecklistItEmailDraft;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const items = draft.items;

  const commit = useMutation({
    mutationFn: async ({ text, send }: { text: string; send: boolean }): Promise<ItEmailPublic> => {
      const { email } = await createItEmail(recordIdsOf(items));
      try {
        const saved = (await updateItEmailText(email.id, text)).email;
        return send ? (await markItEmailSent(email.id)).email : saved;
      } catch (cause) {
        // Leave no half-made email holding the records, so pressing again can make it anew.
        await takeBackItEmail(email.id).catch(() => undefined);
        throw cause;
      }
    },
    onSuccess: (email, { send }) => {
      showToast(send ? STR.itEmails.markedSent(email.openItemCount) : STR.itEmails.savedDraft);
      onClose();
    },
    onError: (cause) => {
      setError(cause instanceof ApiError ? cause.message : STR.errors.generic);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
    },
  });

  return (
    <ItEmailDialog
      initialText={draft.text}
      summary={checklistSummary(items)}
      sentLine={null}
      note={items.length === 0 ? STR.itEmails.checklistAllWithIt : null}
      pending={commit.isPending}
      error={error}
      onCancel={onClose}
      onSave={(text) => {
        commit.mutate({ text, send: false });
      }}
      onMarkSent={(text) => {
        commit.mutate({ text, send: true });
      }}
    />
  );
}
